// Fahrten und Kilometer am Ticket. Bewusst eine Zeile pro Fahrt: Datum, Strecke,
// Kilometer, Fahrzeug — mehr braucht die Abrechnung gegenüber dem Eigentümer nicht.
// Läuft als Tab im „Einsatz“-Widget: Titel und Karte liefert das Widget.
import React, { useCallback, useEffect, useState } from 'react';
import { Car, SpinnerGap, Trash, WarningCircle, X } from '@phosphor-icons/react';
import {
  fahrtLoeschen, fahrtSpeichern, ladeFahrten, ladeFahrzeuge, type Fahrt,
} from '../../lib/zeiterfassung/api';
import { tagText } from '../../lib/zeiterfassung/berechnung';
import { AppKachel } from '../ui/AppIconKnopf';

interface Props {
  ticketId: string;
  userProfile?: { id?: string } | null;
  /** Anzahl Fahrten (Zähler im Einsatz-Tab) */
  onAnzahl?: (n: number) => void;
}

const FELD: React.CSSProperties = {
  height: 38, borderRadius: 11, padding: '0 10px', border: 'none', outline: 'none', background: 'var(--bg)',
  boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13.5, minWidth: 0, width: '100%',
};
const LABEL: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--ink3)' };
const BADGE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 11, fontSize: 11, fontWeight: 800,
  background: 'var(--fill2)', color: 'var(--ink2)',
};

const TicketFahrten: React.FC<Props> = ({ ticketId, userProfile, onAnzahl }) => {
  const meineId = userProfile?.id || '';
  const [fahrten, setFahrten] = useState<Fahrt[]>([]);
  const [fahrzeuge, setFahrzeuge] = useState<string[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState<{ datum: string; von: string; nach: string; km: string; minuten: string; fahrzeug: string } | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true);
    const [f, fz] = await Promise.all([ladeFahrten({ ticketId }), ladeFahrzeuge()]);
    setFahrten(f);
    setFahrzeuge(fz);
    setLaedt(false);
  }, [ticketId]);

  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { onAnzahl?.(fahrten.length); }, [fahrten.length, onAnzahl]);

  const summe = fahrten.reduce((s, f) => s + Number(f.kilometer), 0);
  const fahrtMinuten = fahrten.reduce((s, f) => s + (Number(f.minuten) || 0), 0);

  const speichern = async () => {
    if (!neu || !meineId) return;
    const km = Number(neu.km.replace(',', '.'));
    if (!Number.isFinite(km) || km <= 0) { setFehler('Bitte Kilometer eintragen.'); return; }
    setArbeitet(true);
    const problem = await fahrtSpeichern({
      mitarbeiterId: meineId, ticketId, datum: neu.datum,
      vonOrt: neu.von, nachOrt: neu.nach, kilometer: km, fahrzeug: neu.fahrzeug,
      minuten: neu.minuten.trim() ? Math.round(Number(neu.minuten.replace(',', '.'))) : null,
    });
    setArbeitet(false);
    if (problem) { setFehler(problem); return; }
    setNeu(null);
    setFehler(null);
    await laden();
  };

  const loeschen = async (f: Fahrt) => {
    if (!window.confirm(`Fahrt vom ${tagText(f.datum)} löschen?`)) return;
    await fahrtLoeschen(f.id);
    await laden();
  };

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {(summe > 0 || fahrtMinuten > 0) && (
        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          {summe > 0 && <span className="tabular-nums" data-tip="Kilometer – Summe aller Fahrten" style={BADGE}>{summe.toLocaleString('de-DE')} km</span>}
          {fahrtMinuten > 0 && (
            <span className="tabular-nums" data-tip="Fahrtzeit – Summe der reinen Fahrtzeit" style={BADGE}>
              {Math.floor(fahrtMinuten / 60)}:{String(fahrtMinuten % 60).padStart(2, '0')} h Fahrtzeit
            </span>
          )}
        </div>
      )}

      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 12.5, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" />
          <span className="flex-1">{fehler}</span>
          <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>
            <X size={13} weight="bold" />
          </button>
        </div>
      )}

      {neu && (
        <div className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1.5px var(--acc)' }}>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Von</span>
              <input value={neu.von} onChange={(e) => setNeu({ ...neu, von: e.target.value })} placeholder="von" style={FELD} data-tip="Von – Startort der Fahrt" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Nach</span>
              <input value={neu.nach} onChange={(e) => setNeu({ ...neu, nach: e.target.value })} placeholder="nach" style={FELD} data-tip="Nach – Ziel der Fahrt" /></label>
          </div>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: '1.4fr 1fr 1fr' }}>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Tag</span>
              <input type="date" value={neu.datum} onChange={(e) => setNeu({ ...neu, datum: e.target.value })} style={FELD} data-tip="Tag – An welchem Tag wurde gefahren?" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Kilometer</span>
              <input value={neu.km} onChange={(e) => setNeu({ ...neu, km: e.target.value })} placeholder="km" inputMode="decimal" style={FELD} data-tip="Kilometer – Gefahrene Strecke" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Fahrzeit</span>
              <input value={neu.minuten} onChange={(e) => setNeu({ ...neu, minuten: e.target.value })} placeholder="Min." inputMode="numeric" style={FELD} data-tip="Fahrzeit – In Minuten" /></label>
          </div>
          <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Fahrzeug</span>
            <input value={neu.fahrzeug} onChange={(e) => setNeu({ ...neu, fahrzeug: e.target.value })} placeholder="Fahrzeug" list="fahrzeug-vorschlaege"
              style={FELD} data-tip="Fahrzeug – Dienstwagen oder Privat-PKW" /></label>
          <datalist id="fahrzeug-vorschlaege">
            {fahrzeuge.map((f) => <option key={f} value={f} />)}
          </datalist>
          <div className="flex items-center justify-end" style={{ gap: 6 }}>
            <button type="button" onClick={() => setNeu(null)} style={{ height: 34, padding: '0 12px', borderRadius: 17, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }}>
              Abbrechen
            </button>
            <button type="button" onClick={() => void speichern()} disabled={arbeitet} data-tip="Speichern – Fahrt auf das Ticket buchen"
              className="inline-flex items-center justify-center"
              style={{ height: 34, padding: '0 14px', borderRadius: 17, border: 'none', cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, opacity: arbeitet ? 0.6 : 1 }}>
              {arbeitet ? <SpinnerGap size={14} className="animate-spin" /> : 'Speichern'}
            </button>
          </div>
        </div>
      )}

      {laedt ? (
        <div className="flex justify-center" style={{ padding: '16px 0', color: 'var(--ink3)' }}><SpinnerGap size={16} className="animate-spin" /></div>
      ) : fahrten.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Keine Fahrten erfasst.</p>
      ) : (
        <div className="flex flex-col">
          {fahrten.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center" style={{ gap: '4px 10px', padding: '8px 2px', borderBottom: '1px solid var(--line)', fontSize: 12.5 }}>
              <span className="shrink-0" style={{ fontWeight: 800 }}>{tagText(f.datum)}</span>
              <span className="min-w-0 flex-1 truncate" style={{ color: 'var(--ink2)', fontWeight: 600 }}>
                {[f.von_ort, f.nach_ort].filter(Boolean).join(' → ') || <span style={{ fontStyle: 'italic', color: 'var(--ink3)' }}>ohne Strecke</span>}
              </span>
              {!!f.minuten && <span className="shrink-0 tabular-nums" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{f.minuten} Min.</span>}
              {f.fahrzeug && <span className="shrink-0" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{f.fahrzeug}</span>}
              <span className="shrink-0 text-right tabular-nums" style={{ minWidth: 56, fontWeight: 800 }}>
                {Number(f.kilometer).toLocaleString('de-DE')} km
              </span>
              {f.mitarbeiter_id === meineId && (
                <button type="button" aria-label="Löschen" data-tip="Löschen" onClick={() => void loeschen(f)}
                  className="inline-flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                  <Trash size={13} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!neu && (
        <AppKachel icon={Car} farbe="blau" titel="Fahrt hinzufügen" unter="Kilometer und Fahrzeit"
          tip="Fahrt hinzufügen – Fahrt auf das Ticket buchen"
          onClick={() => setNeu({ datum: new Date().toISOString().slice(0, 10), von: '', nach: '', km: '', minuten: '', fahrzeug: fahrzeuge[0] || '' })} />
      )}
    </div>
  );
};

export default TicketFahrten;
